import { useState, useCallback, useEffect, useRef } from "react";

const getStorageKey = (userId) => `viewed_challenges:${userId || "anon"}`;

function readViewedChallenges(key) {
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function useViewedChallenges(userId) {
  const storageKey = getStorageKey(userId);

  const [viewedChallenges, setViewedChallenges] = useState(() => {
    if (typeof window === "undefined") return [];
    return readViewedChallenges(storageKey);
  });

  const lastSerializedRef = useRef("");

  const writeViewedChallenges = useCallback(
    (next) => {
      try {
        const serialized = JSON.stringify(next);
        localStorage.setItem(storageKey, serialized);
        lastSerializedRef.current = serialized;
      } catch {
        // no-op
      }
    },
    [storageKey]
  );

  // Quand l'utilisateur change, on recharge depuis la bonne clé
  useEffect(() => {
    const latest = readViewedChallenges(storageKey);
    try {
      lastSerializedRef.current = JSON.stringify(latest);
    } catch {
      lastSerializedRef.current = "[]";
    }
    setViewedChallenges(latest);
  }, [storageKey]);

  // Resync (multi-onglets + même onglet)
  useEffect(() => {
    if (typeof window === "undefined") return;

    const resync = () => {
      const latest = readViewedChallenges(storageKey);
      let latestSerialized = "[]";
      try {
        latestSerialized = JSON.stringify(latest);
      } catch {
        latestSerialized = "[]";
      }

      if (latestSerialized !== lastSerializedRef.current) {
        lastSerializedRef.current = latestSerialized;
        setViewedChallenges(latest);
      }
    };

    const onStorage = (e) => {
      if (e.key === storageKey) resync();
    };

    window.addEventListener("storage", onStorage);
    const interval = window.setInterval(resync, 800);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.clearInterval(interval);
    };
  }, [storageKey]);

  const markAsViewed = useCallback(
    (challengeId) => {
      if (!challengeId) return;

      setViewedChallenges((prev) => {
        if (prev.includes(challengeId)) return prev;
        const updated = [...prev, challengeId];
        writeViewedChallenges(updated);
        return updated;
      });
    },
    [writeViewedChallenges]
  );

  const markMultipleAsViewed = useCallback(
    (challengeIds) => {
      const ids = Array.isArray(challengeIds) ? challengeIds.filter(Boolean) : [];
      if (ids.length === 0) return;

      setViewedChallenges((prev) => {
        let changed = false;
        const updated = [...prev];

        for (const id of ids) {
          if (!updated.includes(id)) {
            updated.push(id);
            changed = true;
          }
        }

        if (changed) writeViewedChallenges(updated);
        return changed ? updated : prev;
      });
    },
    [writeViewedChallenges]
  );

  const hasViewed = useCallback(
    (challengeId) => !!challengeId && viewedChallenges.includes(challengeId),
    [viewedChallenges]
  );

  return { viewedChallenges, markAsViewed, markMultipleAsViewed, hasViewed };
}
